import React, { useRef, useEffect, useCallback, useState } from 'react'
import { useGLTF, PerspectiveCamera } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'


import
{
  Controls,
  PhysicsObjects,
  InteractableObjects,
  Media,
  useScene,
  SceneLighting,
  SceneManager,
  CameraManager,
  InteractionManager,
  TiltNavigation,
  useWebCrafter,
  useFixedAnimations
} from '@webcrafter/common'
import { LinkBioUI } from './LinkBioUI.jsx'

/**
 * Props for SceneXyz3D component
 * @typedef {Object} SceneXyz3DProps
 * @property {string} path - Path to the GLB file
 * @property {Function} setShowPopup - Function to control popup visibility
 * @property {Function} setPopupContent - Function to set popup content
 * @property {boolean} isDebugging - Whether debug mode is enabled
 * @property {Function} setXyzAPI - Callback to expose the API to parent
 * @property {React.ReactNode} [children] - Optional child components
 */

/**
 * Main 3D scene component that loads and renders a GLB model.
 * Handles camera controls, animations, interactions, and orbit navigation.
 *
 * @param {SceneXyz3DProps} props
 */

export const SceneXyz3D = ({
  path,
  setShowPopup,
  setPopupContent,
  isDebugging,
  children
}) =>
{
  const { camera, scene: threeScene } = useThree()
  const { scene, animations } = useGLTF(path)
  const { mixer, actions } = useFixedAnimations(animations, scene)

  const controlsRef = useRef(null)
  const [ controlsReady, setControlsReady ] = useState(false)
  const { sceneManager, cameraManager, interactionManager, setManagers } = useWebCrafter()
  const uiGroupRef = useRef(null)

  // Callback to set controls ready state when ref is assigned
  const handleControlsRef = useCallback((node) =>
  {
    if (node)
    {
      controlsRef.current = node
      setControlsReady(true)
    }
  }, [])

  /**
   * Initializes all managers when controls are ready.
   */
  const initializeManagers = useCallback(({
    scene,
    controls,
    animations,
    actions,
    mixer,
    camera,
    setShowPopup,
    setPopupContent
  }) =>
  {
    // If already initialized, skip
    if (sceneManager)
    {
      return
    }

    if (scene)
    {
      const camerasToRemove = []
      scene.traverse((child) =>
      {
        if (child.isCamera)
        {
          camerasToRemove.push(child)
        }
      })
      camerasToRemove.forEach((cam) => cam?.removeFromParent())
    }

    // Create new managers
    const newSceneManager = new SceneManager(scene, controls, animations, actions, mixer)
    const newCameraManager = new CameraManager(newSceneManager, controls, camera)
    const newInteractionManager = new InteractionManager(
      setShowPopup,
      setPopupContent,
      newCameraManager.goToSceneZoneByName,
      newSceneManager.playAnimation,
      newSceneManager.playSound
    )
    if (scene.background)
    {
      threeScene.background = scene.background
    }

    // Update context with new managers
    setManagers(newSceneManager, newCameraManager, newInteractionManager)

  }, [
    sceneManager,
    threeScene,
    setManagers
  ])


  // Initialize managers on mount
  useEffect(() =>
  {
    if (controlsReady && scene)
    {
      initializeManagers({
        scene,
        controls: controlsRef.current,
        animations,
        actions,
        mixer,
        camera,
        setShowPopup,
        setPopupContent
      })
    }
  }, [
    controlsReady,
    scene,
    initializeManagers,
    animations,
    actions,
    mixer,
    camera,
    setShowPopup,
    setPopupContent
  ])


  const handleFitToBox = useCallback(() =>
  {
    if (controlsRef.current && uiGroupRef.current)
    {
      const group = uiGroupRef.current

      // Defer if no children yet (wait for LinkBioUI to mount/render)
      if (group.children.length === 0)
      {
        setTimeout(handleFitToBox, 100)
        return
      }

      // Ensure matrices are up to date before calculating bounding box
      group.updateMatrixWorld(true)

      // Calculate the bounding box of the UI group dynamically
      const box = new THREE.Box3().setFromObject(group)

      // Check for valid box (finite coords)
      if (
        !isFinite(box.min.x) || !isFinite(box.min.y) || !isFinite(box.min.z) ||
        !isFinite(box.max.x) || !isFinite(box.max.y) || !isFinite(box.max.z) ||
        box.isEmpty()
      )
      {
        // Retry later if box is invalid (e.g. initial render frame)
        setTimeout(handleFitToBox, 100)
        return
      }

      // fitToBox(box3, enableTransition, options)
      // Add some padding to prevent the UI from touching the screen edges
      controlsRef.current.fitToBox(box, false, {
        paddingLeft: 0.5,
        paddingRight: 0.5,
        paddingTop: 0.5,
        paddingBottom: 0.5
      })
    }
  }, [ threeScene ])

  // Fit camera to the UI box on mount to ensure it's centered with padding
  useEffect(() =>
  {
    if (controlsReady)
    {
      handleFitToBox()
    }
  }, [ controlsReady, handleFitToBox ])

  // Re-fit on resize with debounce
  useEffect(() =>
  {
    let timeoutId

    const onResize = () =>
    {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() =>
      {
        handleFitToBox()
      }, 1000)
    }

    window.addEventListener('resize', onResize)

    return () =>
    {
      window.removeEventListener('resize', onResize)
      clearTimeout(timeoutId)
    }
  }, [ handleFitToBox ])


  return (
    <>
      {/* <PerspectiveCamera makeDefault position={[0, 0, 20]} fov={35} /> */}
      <Controls innerRef={handleControlsRef} />

      {/* Tilt Navigation - Orbital camera movement based on mouse/gyroscope */}
      <TiltNavigation orbitRadius={2} mouseSensitivity={0.5} gyroSensitivity={0.2} />

      {/* 3D Link in Bio UI - Positioned at center */}
      <group ref={uiGroupRef} position={[ 0, 0, 1.5 ]}>
        <LinkBioUI />
      </group>

      <primitive object={scene}>
        {/* Render scene zones for interactables */}

      </primitive>

      {/* Physics simulation objects */}
      {sceneManager && (
        <>
          <PhysicsObjects
            debug={isDebugging}
            sceneManager={sceneManager}
            interactionManager={interactionManager}
            isDebugging={isDebugging}
          />
          <InteractableObjects
            sceneManager={sceneManager}
            interactionManager={interactionManager}
          />
        </>
      )}

      {/* Media elements (video, audio) */}
      <Media
        sceneManager={sceneManager}
        interactionManager={interactionManager}
      />

      {children}

      {/* Scene lighting (environment, default lights) */}
      <SceneLighting scene={scene} sceneManager={sceneManager} />
    </>
  )
}

export default SceneXyz3D
